import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom'; // Import useLocation
import { MenuComponent } from '../../../../../../_metronic/assets/ts/components';
import { initialQueryState, KTSVG } from '../../../../../../_metronic/helpers';
import { useQueryRequest } from '../../core/QueryRequestProvider';
import { useQueryResponse } from '../../core/QueryResponseProvider';
import { FormattedMessage } from 'react-intl';
import axios from 'axios';
import { Service } from '../../../../service/services-list/core/_models';
import { Company } from '../../../../company/companies-list/core/_models';

// Define the type for location state
interface LocationState {
  status?: any;
}

const HawalasListFilter = () => {
  const { updateState } = useQueryRequest();
  const { isLoading } = useQueryResponse();
  const location = useLocation();
  const state = location.state as LocationState | undefined; // Type assertio
  const [service_category_type, setServiceCategoryType] = useState<string | undefined>();
  const [company_id, setCompanyId] = useState<string | undefined>();
  const [status, setStatus] = useState<any>();
  const [service_id, setServiceId] = useState<string | undefined>();

  const API_URL = process.env.REACT_APP_THEME_API_URL;
  const COMPANY_URL = `${API_URL}/admin/companies`;
  const SERVICE_URL = `${API_URL}/admin/services`;

  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [services, setServices] = useState<Service[] | null>(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);

  // Fetch companies and services
  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const response = await axios.get(COMPANY_URL);
        if (response.data.success) {
          setCompanies(response.data.data.companies);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        setLoadingCompanies(false);
      }
    };

    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        const response = await axios.get(SERVICE_URL);
        if (response.data.success) {
          setServices(response.data.data.services);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchCompanies();
    fetchServices();
  }, [COMPANY_URL, SERVICE_URL]);

  // Apply filters automatically if passed via navigation
  useEffect(() => {
    if (state?.status) {
      setStatus(state.status);
      updateState({
        filter: { status: state.status, service_category_type, company_id, service_id },
        ...initialQueryState,
      });
    }
  }, []);

  useEffect(() => {
    MenuComponent.reinitialization();
  }, []);

  const resetData = () => {
    updateState({ filter: undefined, ...initialQueryState });
    setServiceCategoryType(undefined);
    setCompanyId(undefined);
    setStatus(undefined);
    setServiceId(undefined);
  };

  const filterData = () => {
    updateState({
      filter: { service_category_type, company_id, service_id, status },
      ...initialQueryState,
    });
  };

  const renderCompaniesOptions = () => {
    if (loadingCompanies) {
      return <option>Loading companies...</option>;
    }
    if (companies?.length) {
      return companies.map((company) => (
        <option key={company.id} value={company.id}>
          {company.company_name}
        </option>
      ));
    }
    return <option>No companies available</option>;
  };

  const renderServicesOptions = () => {
    if (loadingServices) {
      return <option>Loading services...</option>;
    }
    if (services?.length) {
      return services.map((service) => (
        <option key={service.id} value={service.id}>
          {service.service_category?.category_name} - {service.company?.company_name}
        </option>
      ));
    }
    return <option>No services available</option>;
  };

  return (
    <>
      {/* begin::Filter Button */}
      <button
        disabled={isLoading}
        type="button"
        className="btn btn-light-primary me-3"
        data-kt-menu-trigger="click"
        data-kt-menu-placement="bottom-end"
      >
        <KTSVG path="/media/icons/duotune/general/gen031.svg" className="svg-icon-2" />
        <FormattedMessage id="TABLE.GENERAL.FILTEROPTION" />
      </button>
      {/* end::Filter Button */}
      {/* begin::SubMenu */}
      <div className="menu menu-sub menu-sub-dropdown w-300px w-md-325px" data-kt-menu="true">
        <div className="px-7 py-5">
          <div className="fs-5 text-dark fw-bolder">
            <FormattedMessage id="APP.GENERAL.TABLE.FILTER.OPTIONLIST" />
          </div>
        </div>
        <div className="px-7 py-5" data-kt-hawala-table-filter="form">
          {/* Status Filter */}
          <div className="mb-5">
            <label className="form-label fs-6 fw-bold">
              <FormattedMessage id="BUNDLE.TABLE.FILTER.STATUS" />
            </label>
            <select
              className="form-select form-select-solid fw-bolder"
              onChange={(e) => setStatus(e.target.value)}
              value={status}
            >
              <option value="">Select</option>
              <option value="0">
                <FormattedMessage id="BUNDLE.TABLE.FILTER.PENDING" />
              </option>
              <option value="1">
                <FormattedMessage id="BUNDLE.TABLE.FILTER.CONFIRMED" />
              </option>
              <option value="2">
                <FormattedMessage id="BUNDLE.TABLE.FILTER.REJECTED" />
              </option>
            </select>
          </div>

          {/* Service Category Type Filter */}
          <div className="mb-5">
            <label className="form-label fs-6 fw-bold">
              <FormattedMessage id="BUNDLE.TABLE.FILTER.TYPE" />
            </label>
            <select
              className="form-select form-select-solid fw-bolder"
              onChange={(e) => setServiceCategoryType(e.target.value)}
              value={service_category_type}
            >
              <option value="">Select</option>
              <option value="social">
                <FormattedMessage id="BUNDLE.TABLE.FILTER.SOCIAL" />
              </option>
              <option value="nonsocial">
                <FormattedMessage id="BUNDLE.TABLE.FILTER.NONSOCIAL" />
              </option>
            </select>
          </div>

          {/* Company Filter */}
          <div className="mb-5">
            <label className="form-label fs-6 fw-bold">
              <FormattedMessage id="BUNDLE.TABLE.FILTER.COMPANY" />:
            </label>
            <select
              className="form-select form-select-solid fw-bolder"
              onChange={(e) => setCompanyId(e.target.value)}
              value={company_id}
            >
              <option value="">Select</option>
              {renderCompaniesOptions()}
            </select>
          </div>

          {/* Service Filter */}
          <div className="mb-5">
            <label className="form-label fs-6 fw-bold">
              <FormattedMessage id="BUNDLE.TABLE.FILTER.SERVICE" />
            </label>
            <select
              className="form-select form-select-solid fw-bolder"
              onChange={(e) => setServiceId(e.target.value)}
              value={service_id}
            >
              <option value="">Select</option>
              {renderServicesOptions()}
            </select>
          </div>

          {/* Actions */}
          <div className="d-flex justify-content-end">
            <button
              type="button"
              disabled={isLoading}
              onClick={resetData}
              className="btn btn-light btn-active-light-primary fw-bold me-2 px-6"
            >
              Reset
            </button>
            <button
              disabled={isLoading}
              onClick={filterData}
              type="button"
              className="btn btn-primary fw-bold px-6"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
      {/* end::SubMenu */}
    </>
  );
};

export { HawalasListFilter };
